import { useEffect, useState } from "react";
import coverFontUrl from "./fonts/HalvarBreit-Blk.ttf";
import { API_URL } from "./api.js";
import { logEvent } from "./logger.js";

export const COVER_FONT_FAMILY = "CoverPdfHalvar";
const SOURCE_TIMEOUT_MS = 8000;
let loadedFont = null;
let inFlight = null;
let batchNumber = 0;

function diagnostic(event, payload, level = "info") {
  try {
    logEvent({ source: "ui", event, level,
      message: event, payload });
  } catch (error) { /* diagnostics must not break rendering */ }
}

async function loadSource(url, label, batch) {
  const controller = new AbortController();
  let timer;
  let face = null;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => {
      const error = new Error("Истекло время загрузки шрифта");
      error.name = "TimeoutError";
      reject(error);
      controller.abort();
    }, SOURCE_TIMEOUT_MS);
  });
  const task = (async () => {
    const response = await fetch(url, { signal: controller.signal,
      credentials: "omit", cache: batch > 1 ? "reload" : "default" });
    if (!response.ok) throw new Error(`Шрифт: HTTP ${response.status}`);
    const bytes = await response.arrayBuffer();
    if (!bytes.byteLength) throw new Error("Получен пустой файл шрифта");
    const candidate = new FontFace(COVER_FONT_FAMILY, bytes, { weight: "800" });
    return candidate.load();
  })();
  try {
    face = await Promise.race([task, timeout]);
    document.fonts.add(face);
    if (typeof document.fonts.load === "function") {
      await Promise.race([
        document.fonts.load(`800 16px "${COVER_FONT_FAMILY}"`, "WEDDING НАША СВАДЬБА 2026"),
        timeout,
      ]);
    }
    if (typeof document.fonts.check === "function" &&
        !document.fonts.check(`800 16px "${COVER_FONT_FAMILY}"`)) {
      throw new Error("Загруженный шрифт недоступен для отрисовки");
    }
    diagnostic("cover_font_ready", { source: label, batch });
    return { face, source: label };
  } catch (error) {
    controller.abort();
    if (face && typeof document.fonts.delete === "function") document.fonts.delete(face);
    diagnostic("cover_font_source_failed", { source: label, batch, font_url: url,
      error_name: error.name, detail: String(error.message).slice(0, 250) }, "warn");
    throw error;
  } finally {
    clearTimeout(timer);
  }
}

export function isCoverFontReady() { return !!loadedFont; }

export function ensureCoverFont() {
  if (loadedFont) return Promise.resolve(loadedFont);
  if (inFlight) return inFlight;
  if (typeof FontFace === "undefined" || typeof document === "undefined" || !document.fonts) {
    const error = new Error("Браузер не поддерживает загрузку шрифта предпросмотра");
    error.code = "font_api_unavailable";
    return Promise.reject(error);
  }
  const attempt = (async () => {
    const sources = [[coverFontUrl, "bundled"],
      [`${API_URL.replace(/\/+$/, "")}/cover-font/`, "api"]];
    let lastError;
    for (let cycle = 0; cycle < 3; cycle += 1) {
      if (cycle > 0) await new Promise(resolve => setTimeout(resolve, cycle === 1 ? 1000 : 4000));
      const batch = ++batchNumber;
      for (const [url, label] of sources) {
        try {
          const result = await loadSource(url, label, batch);
          loadedFont = result;
          return result;
        } catch (error) { lastError = error; }
      }
    }
    diagnostic("cover_font_unavailable", { batch: batchNumber, detail: String(lastError?.message || "unknown").slice(0, 250) }, "error");
    throw lastError;
  })();
  inFlight = attempt;
  const clear = () => { if (inFlight === attempt) inFlight = null; };
  attempt.then(clear, clear); // never cache a rejected Promise
  return attempt;
}

export function useCoverFont() {
  const [state, setState] = useState(() => ({
    phase: isCoverFontReady() ? "ready" : "loading", unsupported: false,
  }));
  const [version, setVersion] = useState(0);
  useEffect(() => {
    let active = true;
    if (!isCoverFontReady()) setState({ phase: "loading", unsupported: false });
    ensureCoverFont().then(result => {
      if (active) setState({ phase: "ready", source: result.source, unsupported: false });
    }).catch(error => {
      if (active) setState({ phase: "error", unsupported: error.code === "font_api_unavailable" });
    });
    return () => { active = false; };
  }, [version]);
  useEffect(() => {
    if (state.phase === "ready") return;
    const online = () => setVersion(value => value + 1);
    window.addEventListener("online", online);
    return () => window.removeEventListener("online", online);
  }, [state.phase]);
  return { ...state, retry: () => setVersion(value => value + 1) };
}